import { useEffect } from 'react';
import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import { AccountTransferFromResponse, Box, Card, CircleButton, SubAccountsLists } from 'vopay';
import { Button, Title } from '@/components/UI';
import { useForm } from '@/components/Form/utils';
import {
  AccountTransferRequest,
  AccountTransferSerializedRequest,
  buildAccountTransferRows,
} from './buildAccountTransferRows.helper';
import { useClientData } from '@/providers/ClientData.provider';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import usePostAccountTransferFrom from '@/hooks/useApi/usePostAccountTransferFrom';
import usePostAccountTransferTo from '@/hooks/useApi/usePostAccountTransferTo';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface AccountTransferProps {
  node: ExtendedNestedSubAccount;
  subAccounts: Record<string, SubAccountsLists>;
  onClose: () => void;
}

const AccountTransfer: React.FC<AccountTransferProps> = ({ node, subAccounts, onClose }) => {
  const {
    response: { AccountID, AccountName, LoginUserName, Success, Country },
  } = useClientData();
  const alertDialog = useAlertDialog();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [accountBalanceResponse, getAccountBalance] = usePostAccountBalance({});
  const [, , , postAccountTransferFrom] = usePostAccountTransferFrom({});
  const [, , , postAccountTransferTo] = usePostAccountTransferTo({});
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });
  const fieldDataList = buildAccountTransferRows({
    MainAccountName: AccountName,
    MainAccountId: AccountID,
    MainAccountBalance: accountBalanceResponse?.AvailableFunds || '0.00',
    subAccounts: subAccounts,
    country: Country,
  });
  const history = useHistory();
  const { renderFormElements, ctx } = useForm<AccountTransferRequest, AccountTransferSerializedRequest>({
    config: fieldDataList,
    initialValues: {
      Amount: '',
      RecipientAccountID: '',
      DebitorAccountID: node.AccountID,
      Currency: node.Currency,
    },
    onSubmit: async (serializedData) => {
      try {
        setDataFetchingInfo(true);
        const fromMain = AccountID === serializedData.DebitorAccountID;
        const toMain = AccountID === serializedData.RecipientAccountID;

        let respFrom: AccountTransferFromResponse | undefined;
        let respTo: AccountTransferFromResponse | undefined;

        if (!fromMain) {
          respFrom = await postAccountTransferFrom(serializedData); // debtor to main
          if (!respFrom.Success) {
            throw new Error(respFrom.ErrorMessage);
          }
        } else if (!toMain) {
          respTo = await postAccountTransferTo(serializedData); // from main to other
          if (!respTo.Success) {
            throw new Error(respTo.ErrorMessage);
          }
        }

        if (fromMain && respTo) {
          history.push({
            pathname: `/transactions-history/details/AccountTransfer/${respTo.TransactionID}/${serializedData.Currency}/`,
            state: {
              transactionId: respTo.TransactionID || '',
              activeModule: 'accountTransfer',
              hasNewTransactionButton: true,
            },
          });
        } else if (respFrom) {
          history.push({
            pathname: `/transactions-history/details/AccountTransfer/${respFrom.TransactionID}/${serializedData.Currency}/`,
            state: {
              transactionId: respFrom.TransactionID || '',
              activeModule: 'accountTransfer',
              hasNewTransactionButton: true,
            },
          });
        }
        onClose();
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('ErrorMessages.defaultMessage');
        console.error('Account Transfer Error', msg);
        alertDialog.error(t('ErrorMessages.transferError'), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
  });
  useEffect(() => {
    if (Success) {
      getAccountBalance({
        PortalUser: LoginUserName,
        Currency: ctx.values.Currency,
      });
    }
  }, [ctx.values.Currency]);
  return (
    <Card hoverEffect={false} style={{ padding: '32px', backgroundColor: 'var(--vopayWhite)' }}>
      <form
        style={{
          minWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
        onSubmit={ctx.handleSubmit}
      >
        <Box flx="col" gapSize="15px">
          <Box jc="fe">
            <CircleButton onClick={onClose} icon="close" />
          </Box>
          <Title level="3">{t('title')}</Title>
        </Box>

        {renderFormElements({
          config: fieldDataList,
        })}
        <Button type="submit" colorScheme="primary" disabled={!ctx.isValid}>
          {t('submitButton')}
        </Button>
      </form>
    </Card>
  );
};

export default AccountTransfer;
